/**
 * Technician Performance Report
 *
 * KPIs: total jobs, active techs, avg jobs/tech, revenue attributed
 * Chart: bar chart — jobs completed per technician
 * Table: tech | jobs | revenue | avg job value
 */
import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell,
} from 'recharts';
import {
  Wrench, Users, Award, DollarSign,
  ArrowLeft, RefreshCw, AlertCircle,
} from 'lucide-react';
import { Button }      from '../components/ui/button';
import { toast }       from 'sonner';
import axios           from 'axios';
import ChartShell      from '../features/reports/components/ChartShell';
import KpiCard         from '../features/reports/components/KpiCard';
import PeriodSelector  from '../features/reports/components/PeriodSelector';

const API = process.env.REACT_APP_BACKEND_URL;

async function getAuthToken() {
  const { getAuth } = await import('firebase/auth');
  const user = getAuth().currentUser;
  if (!user) throw new Error('Not authenticated');
  return user.getIdToken();
}

const fmtFull = (n) =>
  Number(n).toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });

// One color per technician (up to 8, then cycles)
const TECH_COLORS = [
  '#f26b1d', '#3b82f6', '#22c55e', '#a855f7', '#f59e0b', '#ef4444', '#14b8a6', '#ec4899',
];

const CustomBarTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-white border border-gray-200 rounded-lg shadow-md p-3 text-xs">
      <p className="font-semibold text-gray-700 mb-1">{label}</p>
      <p style={{ color: payload[0]?.fill }}>{payload[0]?.value} jobs completed</p>
    </div>
  );
};

// ── Technician table ──────────────────────────────────────────────────────────

const TechTable = ({ technicians, topTechId }) => (
  <div className="overflow-x-auto">
    <table className="w-full text-sm" data-testid="technician-table">
      <thead>
        <tr className="border-b border-gray-100">
          <th className="text-left text-xs font-semibold text-gray-500 uppercase tracking-wider py-2 pr-4">
            Technician
          </th>
          <th className="text-right text-xs font-semibold text-gray-500 uppercase tracking-wider py-2 px-3">
            Jobs
          </th>
          <th className="text-right text-xs font-semibold text-gray-500 uppercase tracking-wider py-2 px-3">
            Revenue
          </th>
          <th className="text-right text-xs font-semibold text-gray-500 uppercase tracking-wider py-2 pl-3">
            Avg Job Value
          </th>
        </tr>
      </thead>
      <tbody>
        {technicians.map((row, i) => (
          <tr
            key={row.techId}
            className="border-b border-gray-50 hover:bg-gray-50 transition-colors"
            data-testid={`tech-row-${row.techId}`}
          >
            <td className="py-2.5 pr-4">
              <div className="flex items-center gap-2">
                <span
                  className="w-2.5 h-2.5 rounded-full shrink-0"
                  style={{ background: TECH_COLORS[i % TECH_COLORS.length] }}
                />
                <span className="font-medium text-gray-800">{row.name}</span>
                {i === 0 && row.techId === topTechId && row.jobs > 0 && (
                  <span className="text-[10px] font-semibold bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded-full">
                    Top
                  </span>
                )}
              </div>
            </td>
            <td className="text-right text-gray-700 font-semibold py-2.5 px-3">
              {row.jobs}
            </td>
            <td className="text-right font-semibold text-gray-800 py-2.5 px-3">
              {row.revenue > 0
                ? fmtFull(row.revenue)
                : <span className="text-gray-300">—</span>}
            </td>
            <td className="text-right text-gray-500 py-2.5 pl-3">
              {row.avgJobValue > 0
                ? fmtFull(row.avgJobValue)
                : <span className="text-gray-300">—</span>}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

// ── Page ──────────────────────────────────────────────────────────────────────

const ReportsTechnician = () => {
  const [period,  setPeriod]  = useState('this_month');
  const [data,    setData]    = useState(null);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState(null);

  const fetchData = useCallback(async (p) => {
    setLoading(true);
    setError(null);
    try {
      const token = await getAuthToken();
      const res   = await axios.get(`${API}/api/reports/technician?period=${p}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setData(res.data);
    } catch (err) {
      const msg = err.response?.data?.detail || 'Failed to load technician report';
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(period); }, [period, fetchData]);

  const periodLabel = {
    this_month: 'This Month',
    last_month: 'Last Month',
    last_3m:    'Last 3 Months',
    last_6m:    'Last 6 Months',
    this_year:  'This Year',
  }[period];

  // Top performer by jobs completed
  const topTech = data?.technicians?.filter(t => t.techId !== 'unassigned' && t.jobs > 0)[0];
  const isEmpty = !loading && (data?.totalJobs ?? 0) === 0;

  return (
    <div className="max-w-5xl mx-auto space-y-6 pb-12">

      {/* Header */}
      <div className="flex items-center justify-between pt-2">
        <div className="flex items-center gap-3">
          <Link
            to="/reports"
            className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-700 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Reports
          </Link>
          <span className="text-gray-300">/</span>
          <h1 className="text-xl font-bold text-gray-900">Technician Performance</h1>
        </div>
        <div className="flex items-center gap-3">
          <PeriodSelector value={period} onChange={setPeriod} />
          <Button
            variant="outline"
            size="sm"
            onClick={() => fetchData(period)}
            disabled={loading}
            data-testid="refresh-btn"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </Button>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="technician-kpi-grid">
        <KpiCard
          label={`Jobs Completed — ${periodLabel}`}
          value={loading ? '…' : String(data?.totalJobs ?? 0)}
          sub="Across all technicians"
          icon={Wrench}
          iconColor="text-orange-500"
          iconBg="bg-orange-50"
          loading={loading}
          testId="kpi-total-jobs"
        />
        <KpiCard
          label="Active Technicians"
          value={loading ? '…' : String(data?.activeTechs ?? 0)}
          sub={`Completed jobs in ${periodLabel.toLowerCase()}`}
          icon={Users}
          iconColor="text-blue-500"
          iconBg="bg-blue-50"
          loading={loading}
          testId="kpi-active-techs"
        />
        <KpiCard
          label="Avg Jobs / Tech"
          value={loading ? '…' : String(data?.avgJobsPerTech ?? 0)}
          sub="Per active technician"
          icon={Award}
          iconColor="text-green-600"
          iconBg="bg-green-50"
          loading={loading}
          testId="kpi-avg-jobs"
        />
        <KpiCard
          label="Revenue Attributed"
          value={loading ? '…' : fmtFull(data?.revenueAttributed ?? 0)}
          sub="Paid invoices for period jobs"
          icon={DollarSign}
          iconColor="text-violet-500"
          iconBg="bg-violet-50"
          loading={loading}
          testId="kpi-revenue-attributed"
        />
      </div>

      {/* Top tech callout */}
      {!loading && topTech && data?.activeTechs > 1 && (
        <div
          className="flex items-center gap-3 px-4 py-3 bg-amber-50 border border-amber-200 rounded-xl text-sm"
          data-testid="top-tech-callout"
        >
          <Award className="w-4 h-4 text-amber-600 shrink-0" />
          <span className="text-amber-800">
            <span className="font-semibold">{topTech.name}</span> led the team with{' '}
            <span className="font-semibold">{topTech.jobs} jobs</span> completed this period
            {topTech.revenue > 0 && (
              <span> ({fmtFull(topTech.revenue)} attributed revenue)</span>
            )}.
          </span>
        </div>
      )}

      {/* Bar chart */}
      <ChartShell
        title="Jobs Completed by Technician"
        subtitle={`Completed jobs per team member — ${periodLabel}`}
        loading={loading}
        empty={isEmpty}
      >
        <ResponsiveContainer width="100%" height={260}>
          <BarChart
            data={data?.byTech ?? []}
            margin={{ top: 4, right: 8, left: 0, bottom: 4 }}
          >
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" vertical={false} />
            <XAxis
              dataKey="name"
              tick={{ fontSize: 11, fill: '#9ca3af' }}
              axisLine={false}
              tickLine={false}
            />
            <YAxis
              allowDecimals={false}
              tick={{ fontSize: 11, fill: '#9ca3af' }}
              axisLine={false}
              tickLine={false}
              width={28}
            />
            <Tooltip content={<CustomBarTooltip />} cursor={{ fill: 'rgba(0,0,0,0.04)' }} />
            <Bar dataKey="jobs" name="Jobs" radius={[4, 4, 0, 0]} maxBarSize={56}>
              {(data?.byTech ?? []).map((_, i) => (
                <Cell key={i} fill={TECH_COLORS[i % TECH_COLORS.length]} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </ChartShell>

      {/* Technician breakdown table */}
      {!loading && !isEmpty && data?.technicians?.length > 0 && (
        <div className="bg-white border border-gray-200 rounded-xl p-5 shadow-sm">
          <h3 className="text-sm font-semibold text-gray-900 mb-0.5">Technician Breakdown</h3>
          <p className="text-xs text-gray-400 mb-4">
            Revenue = paid invoices linked to jobs each technician completed
          </p>
          <TechTable technicians={data.technicians} topTechId={topTech?.techId} />
        </div>
      )}

      {isEmpty && (
        <div className="text-center py-12 text-gray-400" data-testid="technician-empty">
          <Wrench className="w-10 h-10 mx-auto mb-3 opacity-30" />
          <p className="text-sm">No completed jobs found for this period.</p>
          <p className="text-xs mt-1">Technician data is available once jobs are marked complete and assigned to a team member.</p>
        </div>
      )}

      {error && (
        <div className="p-4 rounded-lg bg-red-50 border border-red-200 text-sm text-red-700 flex gap-2">
          <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
          {error}
        </div>
      )}
    </div>
  );
};

export default ReportsTechnician;
